/**
 * The board screen (docs/06-UI-SPEC.md §6).
 *
 * The tool rail and the rest of the header arrive with the steps that build them.
 * Two things here are already spec, not scaffolding:
 *
 *  - who gets which control is `boardChrome`, not this file: a student draws with the
 *    same tools the teacher has, while the room code, the class list and the paper
 *    stay hers (SRS B4, SRS D4, §6.4)
 *  - on a phone held upright the canvas takes the whole width, because a 240 px panel
 *    on a 390 px screen leaves nothing to write on. Turned sideways - the way the
 *    spec expects a phone to be used - the panel is there. The full small-screen
 *    layout is step 14.1 and the upright overlay is 14.2.
 */

import { useCallback, useMemo, useState } from 'react';

import { createId } from '@opal/shared';

import { endClass } from '../api.js';
import { Logo } from '../assets/Logo.js';
import { BoardCanvas } from '../board/BoardCanvas.js';
import { boardChrome, type BoardChrome } from '../board/board-chrome.js';
import type { BoardIdentity } from '../board/presence.js';
import { useBoardStore } from '../board/store.js';
import { useShortcuts } from '../board/use-shortcuts.js';
import { useSharedBoard, type SharedBoardWiring } from '../board/use-shared-board.js';
import { ClassCode } from '../components/ClassCode.js';
import { BoardMenu } from '../components/board/BoardMenu.js';
import { ConnectionBanner } from '../components/board/ConnectionBanner.js';
import { EndSessionDialog } from '../components/board/EndSessionDialog.js';
import { SessionEnded } from '../components/board/SessionEnded.js';
import { ParticipantsPanel } from '../components/board/ParticipantsPanel.js';
import { PageTabs } from '../components/board/PageTabs.js';
import { PrivateLayerBadge } from '../components/board/PrivateLayerBadge.js';
import { PropertiesPanel } from '../components/board/PropertiesPanel.js';
import { Scrollbars } from '../components/board/Scrollbars.js';
import { ShortcutSheet } from '../components/board/ShortcutSheet.js';
import { ReturnToPresenter } from '../components/board/ReturnToPresenter.js';
import { ZoomControls } from '../components/board/ZoomControls.js';
import { gradeBandLabel, readGradeBand, topicLabel } from '../lesson.js';
import { navigate, useQueryParam } from '../router.js';

interface BoardHeaderProps {
  readonly title: string;
  /** Null for a student: the code is the teacher's to share. */
  readonly code: string | null;
  /** Null for a student: the menu and its End Session are the teacher's (SRS I1). */
  readonly onEndSession: (() => void) | null;
}

function BoardHeader({ title, code, onEndSession }: BoardHeaderProps) {
  return (
    <header className="flex h-header shrink-0 items-center justify-between gap-4 border-b border-border bg-surface px-4">
      <div className="flex min-w-0 items-center gap-4">
        {onEndSession === null ? null : <BoardMenu onEndSession={onEndSession} />}
        <span className="shrink-0">
          <Logo size={28} />
        </span>
        <span className="truncate text-base font-medium text-text-primary">{title}</span>
      </div>
      {code === null ? null : (
        <div className="shrink-0">
          <ClassCode code={code} compact />
        </div>
      )}
    </header>
  );
}

/**
 * Who this device is on the board.
 *
 * A student is whoever typed their name on the join screen, and carries the place in
 * the class they were given then. Anyone else here is the teacher, and a teacher is
 * always shown to the class as "Teacher" (SRS D2).
 */
function useIdentity(name: string | null, participantId: string | null): BoardIdentity {
  return useMemo(
    () => ({
      // A student's id is their place in the class, so their work and their cursor
      // are still theirs after a reconnection.
      userId: participantId ?? createId('usr'),
      displayName: name ?? 'Teacher',
      role: name === null ? 'teacher' : 'student',
      participantId,
    }),
    [name, participantId],
  );
}

/**
 * Shown to a student the teacher has removed from the class (SRS D4).
 *
 * Their work stays on the teacher's board; this screen is only about them, so it says
 * what happened and gives them somewhere to go.
 */
function RemovedFromClass() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-surface-sunken p-6 text-center">
      <Logo size={40} />
      <p className="text-lg text-text-primary">Your teacher has ended your place in this class.</p>
      <p className="text-base text-text-secondary">Everything you drew is still on their board.</p>
      <button
        type="button"
        onClick={() => {
          navigate('/join');
        }}
        className="h-touch rounded-md bg-primary px-6 text-base font-medium text-text-inverse transition duration-fast ease-out hover:bg-primary-hover"
      >
        Join another class
      </button>
    </main>
  );
}

interface BoardStageProps {
  readonly identity: BoardIdentity;
  readonly board: SharedBoardWiring;
  readonly chrome: BoardChrome;
}

/** The canvas and everything that floats over it (§6.6, §6.7, §17.3, SRS D6). */
function BoardStage({ identity, board, chrome }: BoardStageProps) {
  return (
    <main className="relative min-w-0 flex-1">
      <BoardCanvas
        userId={identity.userId}
        onReady={board.onReady}
        onPointerMove={board.onPointerMove}
      />
      <PrivateLayerBadge show={chrome.layers} />
      <Scrollbars />
      <ZoomControls />
      <ConnectionBanner notice={board.notice} onRetry={board.onRetry} />
      <ReturnToPresenter show={board.canReturn} onReturn={board.returnToPresenter} />
    </main>
  );
}

interface SidePanelsProps {
  readonly chrome: BoardChrome;
  readonly board: SharedBoardWiring;
  readonly boardId: string | null;
}

/**
 * The right-hand column: properties above, the class list below (§6.3, §6.4).
 *
 * Away entirely on a phone held upright - see the note at the top of this file.
 */
function SidePanels({ chrome, board, boardId }: SidePanelsProps) {
  if (!chrome.tools) return null;
  return (
    <div className="flex min-h-0 flex-col overflow-y-auto max-[640px]:hidden">
      <div className="min-h-0 flex-1">
        <PropertiesPanel
          onUndo={board.undo}
          onRedo={board.redo}
          showPaper={chrome.paper}
          showLayers={chrome.layers}
        />
      </div>
      {chrome.participants ? (
        <ParticipantsPanel
          boardId={boardId}
          online={board.online}
          presenting={board.presenting}
          onPresentingChange={board.setPresenting}
        />
      ) : null}
    </div>
  );
}

/**
 * SRS I1 step 4 — the teacher ending the lesson, from the tap to the finished screen.
 *
 * `ended` is kept here as well as read from the server's word on the connection.
 * The two say the same thing by two roads, and the road that cannot fail is the one
 * that answered the teacher's own request.
 */
function useEndSession(boardId: string | null) {
  const [asking, setAsking] = useState(false);
  const [ended, setEnded] = useState(false);

  const confirm = useCallback(async (): Promise<string | null> => {
    if (!boardId) return "This board has no class to end.";
    const result = await endClass(boardId);
    if (!result.ok) return result.message;
    setAsking(false);
    setEnded(true);
    return null;
  }, [boardId]);

  const cancel = useCallback(() => {
    setAsking(false);
  }, []);

  const ask = useCallback(() => {
    setAsking(true);
  }, []);

  return { asking, ended, ask, cancel, confirm };
}

/**
 * docs/06-UI-SPEC.md §16 - the whole shortcut set, and the sheet that lists it.
 *
 * Undo, redo and turning the page are the board's to do, so they are handed down;
 * everything else a shortcut does, it does through the store.
 */
function useBoardShortcuts(board: SharedBoardWiring) {
  return useShortcuts(
    useMemo(
      () => ({
        undo: board.undo,
        redo: board.redo,
        goToPage: (pageId: string) => {
          board.pages.goTo(pageId);
        },
      }),
      [board.undo, board.redo, board.pages],
    ),
  );
}

export function BoardPage() {
  const topic = useQueryParam('topic');
  const grade = useQueryParam('grade');
  const code = useQueryParam('code');
  const name = useQueryParam('name');
  const boardId = useQueryParam('board');
  const isStudent = name !== null;
  const identity = useIdentity(name, useQueryParam('pid'));
  const board = useSharedBoard(identity, boardId);
  const access = useBoardStore((state) => state.access);
  const pages = useBoardStore((state) => state.pages);
  const activePageId = useBoardStore((state) => state.activePageId);
  const shortcuts = useBoardShortcuts(board);
  const session = useEndSession(boardId);

  const title = isStudent
    ? `${topicLabel(topic)} · ${name}`
    : `${topicLabel(topic)} · ${gradeBandLabel(readGradeBand(grade))}`;

  if (isStudent && access === 'removed') return <RemovedFromClass />;
  // SRS I1 step 5 — the lesson is filed. Whoever is still looking at it is told, and
  // the board itself goes, because there is nothing left that this screen can save.
  if (access === 'ended' || session.ended) {
    return <SessionEnded role={isStudent ? 'student' : 'teacher'} />;
  }

  const chrome = boardChrome(identity.role, access);

  return (
    <div className="flex h-screen flex-col bg-surface-sunken">
      <BoardHeader
        title={title}
        code={chrome.classCode ? code : null}
        onEndSession={chrome.boardMenu ? session.ask : null}
      />
      <div className="flex min-h-0 flex-1">
        <BoardStage identity={identity} board={board} chrome={chrome} />
        <SidePanels chrome={chrome} board={board} boardId={boardId} />
      </div>
      <PageTabs
        pages={pages}
        activePageId={activePageId}
        actions={board.pages}
        canManage={chrome.pageAdmin}
      />
      <ShortcutSheet open={shortcuts.helpOpen} onClose={shortcuts.closeHelp} />
      {session.asking ? (
        <EndSessionDialog onConfirm={session.confirm} onCancel={session.cancel} />
      ) : null}
    </div>
  );
}
